import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Card, Row, Col, Button } from 'react-bootstrap';
import {
  ArrowLeft,
  Info,
  DollarSign,
  Clock,
  Shield,
  TrendingUp,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';

const LoanDetails = () => {
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [openFaqIndex, setOpenFaqIndex] = useState(null);
  const [loanPolicies] = useState({
    minLoanAmount: 100,
    maxLoanAmount: 50000,
    maxLoanTermMonths: 60,
    loanTypes: ['PERSONAL', 'TRADING_CAPITAL', 'EMERGENCY', 'BUSINESS'],
  });

  const loanTypeDescriptions = {
    PERSONAL: {
      title: 'Personal Loan',
      description: 'For personal expenses and needs',
      icon: '👤',
      color: '#667eea',
    },
    TRADING_CAPITAL: {
      title: 'Trading Capital',
      description: 'To boost your crypto trading portfolio',
      icon: '📈',
      color: '#00C851',
    },
    EMERGENCY: {
      title: 'Emergency Loan',
      description: 'Quick access for urgent financial needs',
      icon: '🚨',
      color: '#f59e0b',
    },
    BUSINESS: {
      title: 'Business Expansion',
      description: 'For growing your business ventures',
      icon: '💼',
      color: '#764ba2',
    },
  };

  const features = [
    {
      icon: DollarSign,
      title: 'Flexible Amounts',
      description: `Borrow from $${loanPolicies.minLoanAmount.toLocaleString()} to $${loanPolicies.maxLoanAmount.toLocaleString()}`,
      color: '#667eea',
    },
    {
      icon: Clock,
      title: 'Extended Terms',
      description: `Up to ${loanPolicies.maxLoanTermMonths} months repayment period`,
      color: '#00C851',
    },
    {
      icon: Shield,
      title: 'Secure Process',
      description: 'Bank-level security for your application',
      color: '#f59e0b',
    },
    {
      icon: TrendingUp,
      title: 'Competitive Rates',
      description: 'Interest rates based on your credit score',
      color: '#764ba2',
    },
  ];

  const requirements = [
    'Active account in good standing',
    'Verified identity (KYC completed)',
    'Minimum credit score requirements',
    'Valid collateral or demo balance',
    'Clear purpose for loan usage',
  ];

  const faqItems = [
    {
      question: 'How long does approval take?',
      answer: 'Most loan applications are reviewed within 24-48 hours.',
    },
    {
      question: 'What are the interest rates?',
      answer:
        'Interest rates are determined based on your credit score, loan amount, collateral type, and repayment term.',
    },
    {
      question: 'Can I repay early?',
      answer:
        'Yes, early repayment is available with reduced interest charges.',
    },
    {
      question: 'What happens if my application is rejected?',
      answer:
        'You will receive detailed reasons and can reapply after addressing the concerns.',
    },
  ];

  // Handle window resize for mobile detection
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <div
      style={{
        backgroundColor: '#FFFFFF',
        minHeight: '100vh',
      }}
    >
      {/* Header with full-width background */}
      <div
        style={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          width: '100%',
        }}
      >
        <Container
          fluid
          style={{
            maxWidth: isMobile ? '100%' : '85%',
            margin: '0 auto',
            padding: isMobile ? '0' : '0 1rem',
          }}
        >
          <div className='d-flex justify-content-between align-items-center p-3'>
            <Button
              variant='link'
              onClick={() => navigate(-1)}
              className='p-0'
              aria-label='Go back'
              style={{ color: '#FFFFFF' }}
            >
              <ArrowLeft size={24} />
            </Button>
            <h5 className='mb-0 fw-bold' style={{ color: '#FFFFFF' }}>
              Loan Details
            </h5>
            <div style={{ width: '24px' }}></div>
          </div>
        </Container>
      </div>

      {/* Main Content with constrained width */}
      <Container
        fluid
        style={{
          maxWidth: isMobile ? '100%' : '85%',
          padding: 'clamp(1rem, 3vw, 1.5rem)',
          backgroundColor: '#FFFFFF',
          minHeight: '100vh',
          margin: '0 auto',
        }}
      >
        {/* Content Section */}
        <div style={{ paddingBottom: '100px' }}>
          {/* Info Card */}
          <Card
            className='border-0 shadow-sm mb-4'
            style={{ backgroundColor: '#f0f4ff' }}
          >
            <Card.Body className='p-3 d-flex align-items-start'>
              <div
                className='rounded-circle me-3 d-flex align-items-center justify-content-center flex-shrink-0'
                style={{
                  width: '40px',
                  height: '40px',
                  backgroundColor: '#667eea',
                }}
              >
                <Info size={20} color='white' />
              </div>
              <div className='flex-grow-1'>
                <h6 className='fw-bold text-dark mb-1'>
                  Welcome to Our Loan Program
                </h6>
                <p className='text-muted small mb-0'>
                  Access flexible financing options tailored to your needs.
                  Learn about our loan policies, requirements, and how to apply.
                </p>
              </div>
            </Card.Body>
          </Card>

          {/* Loan Limits */}
          <div className='mb-4'>
            <h6 className='fw-bold text-dark mb-3'>Loan Limits</h6>
            <Row className='g-3'>
              <Col xs={6} md={4}>
                <Card className='border-0 shadow-sm h-100'>
                  <Card.Body className='text-center p-3'>
                    <div
                      className='mb-2'
                      style={{ fontSize: '1.5rem', color: '#667eea' }}
                    >
                      💵
                    </div>
                    <div className='fw-bold text-dark mb-1'>
                      ${loanPolicies.minLoanAmount.toLocaleString()}
                    </div>
                    <small className='text-muted'>Minimum Amount</small>
                  </Card.Body>
                </Card>
              </Col>
              <Col xs={6} md={4}>
                <Card className='border-0 shadow-sm h-100'>
                  <Card.Body className='text-center p-3'>
                    <div
                      className='mb-2'
                      style={{ fontSize: '1.5rem', color: '#00C851' }}
                    >
                      💰
                    </div>
                    <div className='fw-bold text-dark mb-1'>
                      ${loanPolicies.maxLoanAmount.toLocaleString()}
                    </div>
                    <small className='text-muted'>Maximum Amount</small>
                  </Card.Body>
                </Card>
              </Col>
              <Col xs={12} md={4}>
                <Card className='border-0 shadow-sm h-100'>
                  <Card.Body className='text-center p-3'>
                    <div
                      className='mb-2'
                      style={{ fontSize: '1.5rem', color: '#f59e0b' }}
                    >
                      ⏱️
                    </div>
                    <div className='fw-bold text-dark mb-1'>
                      {loanPolicies.maxLoanTermMonths} Months
                    </div>
                    <small className='text-muted'>Maximum Term</small>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </div>

          {/* Loan Types */}
          <div className='mb-4'>
            <h6 className='fw-bold text-dark mb-3'>Available Loan Types</h6>
            <Row className='g-3'>
              {loanPolicies.loanTypes.map((type) => {
                const info = loanTypeDescriptions[type];
                return (
                  <Col xs={12} sm={6} key={type}>
                    <Card className='border-0 shadow-sm hover-card h-100'>
                      <Card.Body className='p-3'>
                        <div className='d-flex align-items-start'>
                          <div
                            className='rounded-circle me-3 d-flex align-items-center justify-content-center'
                            style={{
                              width: '50px',
                              height: '50px',
                              backgroundColor: `${info.color}20`,
                              fontSize: '1.5rem',
                            }}
                          >
                            {info.icon}
                          </div>
                          <div className='flex-grow-1'>
                            <h6
                              className='fw-bold mb-1'
                              style={{ fontSize: '0.95rem' }}
                            >
                              {info.title}
                            </h6>
                            <p className='text-muted small mb-0'>
                              {info.description}
                            </p>
                          </div>
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>
                );
              })}
            </Row>
          </div>

          {/* Key Features */}
          <div className='mb-4'>
            <h6 className='fw-bold text-dark mb-3'>Key Features</h6>
            <Row className='g-3'>
              {features.map((feature, index) => {
                const IconComponent = feature.icon;
                return (
                  <Col xs={12} sm={6} key={index}>
                    <Card className='border-0 shadow-sm h-100'>
                      <Card.Body className='p-3 d-flex align-items-start'>
                        <div
                          className='rounded-circle me-3 d-flex align-items-center justify-content-center flex-shrink-0'
                          style={{
                            width: '45px',
                            height: '45px',
                            backgroundColor: `${feature.color}20`,
                          }}
                        >
                          <IconComponent size={20} color={feature.color} />
                        </div>
                        <div>
                          <h6
                            className='fw-semibold mb-1'
                            style={{ fontSize: '0.9rem' }}
                          >
                            {feature.title}
                          </h6>
                          <p className='text-muted small mb-0'>
                            {feature.description}
                          </p>
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>
                );
              })}
            </Row>
          </div>

          {/* Requirements */}
          <div className='mb-4'>
            <h6 className='fw-bold text-dark mb-3'>Eligibility Requirements</h6>
            <Card className='border-0 shadow-sm'>
              <Card.Body className='p-3'>
                {requirements.map((req, index) => (
                  <div
                    key={index}
                    className='d-flex align-items-center mb-2 pb-2'
                    style={{
                      borderBottom:
                        index !== requirements.length - 1
                          ? '1px solid #f0f0f0'
                          : 'none',
                    }}
                  >
                    <div
                      className='rounded-circle me-3 d-flex align-items-center justify-content-center'
                      style={{
                        width: '24px',
                        height: '24px',
                        backgroundColor: '#00C85120',
                        flexShrink: 0,
                      }}
                    >
                      <span style={{ color: '#00C851', fontSize: '0.75rem' }}>
                        ✓
                      </span>
                    </div>
                    <span className='small'>{req}</span>
                  </div>
                ))}
              </Card.Body>
            </Card>
          </div>

          {/* FAQ */}
          <div className='mb-4'>
            <h6 className='fw-bold text-dark mb-3'>
              Frequently Asked Questions
            </h6>
            <div className='d-flex flex-column gap-3'>
              {faqItems.map((faq, index) => (
                <Card key={index} className='border-0 shadow-sm'>
                  <Card.Body className='p-0'>
                    <div
                      className='d-flex justify-content-between align-items-center p-3'
                      style={{ cursor: 'pointer' }}
                      onClick={() =>
                        setOpenFaqIndex(openFaqIndex === index ? null : index)
                      }
                    >
                      <h6
                        className='fw-semibold mb-0'
                        style={{ fontSize: '0.9rem' }}
                      >
                        {faq.question}
                      </h6>
                      {openFaqIndex === index ? (
                        <ChevronUp
                          size={20}
                          style={{ color: '#667eea', flexShrink: 0 }}
                        />
                      ) : (
                        <ChevronDown
                          size={20}
                          style={{ color: '#667eea', flexShrink: 0 }}
                        />
                      )}
                    </div>
                    {openFaqIndex === index && (
                      <div
                        className='px-3 pb-3'
                        style={{
                          borderTop: '1px solid #f0f0f0',
                          paddingTop: '12px',
                          animation: 'fadeIn 0.3s ease-in-out',
                        }}
                      >
                        <p className='text-muted small mb-0'>{faq.answer}</p>
                      </div>
                    )}
                  </Card.Body>
                </Card>
              ))}
            </div>
          </div>

          {/* Apply Button */}
          <Button
            onClick={() => navigate('/apply-loan')}
            className='w-100 py-3 fw-semibold'
            style={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              border: 'none',
              borderRadius: '12px',
              boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)',
              transition: 'all 0.3s ease',
            }}
          >
            Apply for Loan Now
          </Button>
        </div>
      </Container>

      <style jsx>{`
        .hover-card {
          transition: transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out;
        }
        .hover-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15) !important;
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @media (max-width: 576px) {
          h5 {
            font-size: 1.1rem !important;
          }
          h6 {
            font-size: 1rem !important;
          }
        }

        @media (max-width: 380px) {
          h5 {
            font-size: 1rem !important;
          }
          h6 {
            font-size: 0.95rem !important;
          }
        }
      `}</style>
    </div>
  );
};

export default LoanDetails;
